import { useState } from "react";
import {
  DownCircleFilled,
  SearchOutlined,
  PlayCircleOutlined,
  EyeOutlined,
  CopyOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { Dropdown, message, Popconfirm, Tag } from "antd";
import { useColumnToggle } from "../../helper/useColumnToggle";
import {
  deleteMasterSuiteData,
  getMasterSuiteStepsByName,
} from "../../service/mastersuite.service";
import SearchFilter from "../../components/generic/SearchFilter";
import COLORS from "../../styles/colors";
import { formatDisplayDate, parseDateString } from "../../helper/date_helper";

const OPTIONAL_MS_LIBRARY_COLUMNS = ["comments", "createdDate", "createdBy"];

// Run Result status → tag colour (mirrors the Job Status tag on the Jobs page)
const RUN_RESULT_COLORS = {
  Completed: COLORS.COMPLETED,
  "In Progress": COLORS.IN_PROGRESS,
  Queued: COLORS.QUEUED,
  Error: COLORS.ERROR,
  "Completed with Error": COLORS.WITH_ERROR,
};

/**
 * Library grid: columns, optional column toggle, and Run modal state.
 * Renders via TableComponent in MasterSuite (same pattern as Task — no resizable widths).
 */
export function useMasterSuiteLibraryGrid({
  embed,
  handleViewModal,
  handleCopyModal,
  handleEditModal,
  getAllLibraryData,
}) {
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [masterSuiteName, setMasterSuiteName] = useState("");
  const [defaultEnvironment, setDefaultEnvironment] = useState("");
  const [masterSuiteSteps, setMasterSuiteSteps] = useState(0);
  const [stepData, setStepData] = useState([]);

  const handleViewClick = (record) => {
    handleViewModal(record.name, record.defaultEnvironment);
  };
  const handleDeleteClick = (record) => {
    deleteMasterSuiteData(record.name)
      .then(() => {
        message.success(`Deleted ${record.name} successfully`);
        getAllLibraryData();
      })
      .catch((err) => {
        // Prefer the backend's reason (e.g. "… is scheduled and cannot be deleted")
        // over axios's generic "Request failed with status code 400".
        const serverMessage = err.response?.data?.message;
        message.error(
          serverMessage || `Failed to delete master suite: ${err.message}`,
          8,
        );
      });
  };
  const handleRunClick = (record) => {
    setMasterSuiteName(record.name);
    setMasterSuiteSteps(record.steps);
    setDefaultEnvironment(record.defaultEnvironment);
    getMasterSuiteStepsByName(record.name).then((response) => {
      setStepData(response.data);
      setRunModalOpen(true);
    });
  };
  const handleCopyClick = (record) => {
    handleCopyModal(record.name, record.defaultEnvironment);
  };
  const handleEditClick = (record) => {
    handleEditModal(record.name, record.defaultEnvironment);
  };
  const handleRunClose = () => {
    setRunModalOpen(false);
  };

  const columns = [
    {
      title: "Display Name",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <SearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      filterIcon: () => (
        <SearchOutlined style={{ color: "rgb(255,255,255)" }} />
      ),
      onFilter: (value, record) =>
        record.name.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: "Label(s)",
      dataIndex: "label",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Comments",
      dataIndex: "comments",
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <SearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      filterIcon: () => (
        <SearchOutlined style={{ color: "rgb(255,255,255)" }} />
      ),
      onFilter: (value, record) =>
        record.comments.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: "Steps",
      dataIndex: "steps",
      width: 90,
      align: "center",
      sorter: (a, b) => a.steps - b.steps,
    },
    {
      title: "Runs",
      dataIndex: "runs",
      width: 90,
      align: "center",
    },
    {
      title: "Last Run Date",
      dataIndex: "lastRunDate",
      sorter: (a, b) => {
        const firstDate = a.lastRunDate != null ? a.lastRunDate : "";
        const secondDate = b.lastRunDate != null ? b.lastRunDate : "";
        if (!a.lastRunDate && !b.lastRunDate) return 0;
        if (!a.lastRunDate) return 1;
        return firstDate.localeCompare(secondDate);
      },
      render: (date, record) =>
        !record.lastRunStatus
          ? "No Run"
          : record.lastRunStatus === "In Progress"
            ? "In Progress"
            : date,
    },
    {
      title: "Run By",
      dataIndex: "runBy",
      sorter: (a, b) => {
        const firstRunBy = a.runBy != null ? a.runBy : "";
        const secondRunBy = b.runBy != null ? b.runBy : "";
        if (!a.runBy && !b.runBy) return 0;
        if (!a.runBy) return 1;
        return firstRunBy.localeCompare(secondRunBy);
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <SearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      filterIcon: () => (
        <SearchOutlined style={{ color: "rgb(255,255,255)" }} />
      ),
      onFilter: (value, record) =>
        record.runBy.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: "Run Result",
      dataIndex: "lastRunStatus",
      sorter: (a, b) => {
        const firstStatus = a.lastRunStatus != null ? a.lastRunStatus : "";
        const secondStatus = b.lastRunStatus != null ? b.lastRunStatus : "";
        if (!a.lastRunStatus && !b.lastRunStatus) return 0;
        if (!a.lastRunStatus) return 1;
        return firstStatus.localeCompare(secondStatus);
      },
      filters: [
        { text: "Completed", value: "Completed" },
        { text: "Queued", value: "Queued" },
        { text: "Error", value: "Error" },
        { text: "In Progress", value: "In Progress" },
        { text: "Completed with Error", value: "Completed with Error" },
      ],
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <SearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      filterIcon: () => (
        <SearchOutlined style={{ color: "rgb(255,255,255)" }} />
      ),
      onFilter: (value, record) =>
        (record.lastRunStatus || "").toLowerCase().includes(value.toLowerCase()),
      render: (status) =>
        status ? (
          <Tag
            color={RUN_RESULT_COLORS[status]}
            className="ms-run-result-tag"
            style={{ fontWeight: 700 }}
          >
            {status}
          </Tag>
        ) : (
          <Tag className="ms-run-result-tag">No Run</Tag>
        ),
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      sorter: (a, b) =>
        parseDateString(a.createdDate) - parseDateString(b.createdDate),
      defaultSortOrder: "descend",
      render: (v) => formatDisplayDate(v),
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      sorter: (a, b) => a.createdBy.localeCompare(b.createdBy),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <SearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      filterIcon: () => (
        <SearchOutlined style={{ color: "rgb(255,255,255)" }} />
      ),
      onFilter: (value, record) =>
        record.createdBy.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: "Actions",
      dataIndex: "operation",
      className: "ms-library-actions-cell",
      align: "center",
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "1",
                label: "Run",
                icon: <PlayCircleOutlined />,
                onClick: () => handleRunClick(record),
              },
              {
                key: "2",
                label: "View",
                icon: <EyeOutlined />,
                onClick: () => handleViewClick(record),
              },
              !embed && {
                key: "3",
                label: "Copy",
                icon: <CopyOutlined />,
                onClick: () => handleCopyClick(record),
              },
              !embed && {
                key: "4",
                label: "Edit",
                icon: <EditOutlined />,
                onClick: () => handleEditClick(record),
              },
              !embed && { type: "divider" },
              !embed && {
                key: "5",
                danger: true,
                icon: <DeleteOutlined />,
                label: (
                  <Popconfirm
                    title="Are you sure you want to delete this record?"
                    onConfirm={() => handleDeleteClick(record)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <span>Delete</span>
                  </Popconfirm>
                ),
              },
            ],
          }}
          overlayClassName="table-row-actions-dropdown"
        >
          <button type="button" className="table-more-options">
            Options <DownCircleFilled />
          </button>
        </Dropdown>
      ),
    },
  ];

  const { visibleColumns, columnToggleProps } = useColumnToggle(
    columns,
    OPTIONAL_MS_LIBRARY_COLUMNS,
    "arc_cols_mastersuite",
  );

  return {
    mergedColumns: visibleColumns,
    columnToggleProps,
    runModalProps: {
      open: runModalOpen,
      handleCancel: handleRunClose,
      masterSuiteName,
      defaultEnvironment,
      steps: masterSuiteSteps,
      stepData,
      onRunStarted: getAllLibraryData,
    },
  };
}
